import { observable } from "mobx";
import { DateTime } from "luxon";

import { EventType } from "@gp/models";
import { NameProvider } from "@gp/utility";
import { BetOffer } from "./index";
import { EventOffer } from "@gp/offer";
import { format } from "@utils/format";
import { TBettingOffer } from "@api-types/bet-booster/BettingOffer";

export class BettingOffer {
	nameProvider: NameProvider;
	bettingOfferId: string;
	bettingOfferKeyId: string;
	bettingTypeId: string;
	bettingTypeTip: string;
	currentResult: any;
	displayedAmount: string;
	eventId: string;
	id: string;
	indicator: number;
	isBank: boolean;
	isLive: boolean;
	multiEventName: string;
	multiOffers?: BetOffer[];
	playerFirstName: string;
	playerId: string | null;
	playerLastName?: string;
	specifier: null | { [key: string]: string };
	sportData: any;
	sportId?: string;
	submitCount?: number;
	systemIdentifier: string;
	teamGender: number;
	teamId?: string;
	teamName?: string;
	value: number;

	@observable bettingOfferStatus: number;

	constructor(betOffer: TBettingOffer) {
		this.nameProvider = new NameProvider();

		this.bettingOfferId = betOffer.bettingOfferId || "";
		this.bettingOfferKeyId = betOffer.bettingOfferKeyId || "";
		this.bettingTypeId = betOffer.bettingTypeId || "";
		this.bettingTypeTip = betOffer.bettingTypeTip || "";
		this.eventId = betOffer.eventId || "";
		this.playerId = betOffer.playerId;
		this.specifier = betOffer.specifier || null;
		this.sportData = betOffer.sportData || {};
		this.sportData.result = format.safeJsonParse(this.sportData.result, {});
		this.teamId = betOffer.teamId;
		this.value = betOffer.value || 0;
		this.sportId = betOffer.sportId;
	}

	applyTeamNamesToMultiOffers() {
		if (this.multiOffers) {
			this.multiOffers.forEach((multiOffer) => {
				if (!multiOffer.sportData.teamOneName) {
					multiOffer.sportData.teamOneName =
						this.sportData.teamOneName;
				}
				if (!multiOffer.sportData.teamTwoName) {
					multiOffer.sportData.teamTwoName =
						this.sportData.teamTwoName;
				}
			});
		}
	}

	//#region getters

	get formattedBettingOfferStatus() {
		return format.formatBettingOfferStatus(this.bettingOfferStatus);
	}

	get timeString() {
		if (this.sportData.eventStartTime) {
			return DateTime.fromISO(this.sportData.eventStartTime).toFormat(
				"dd.MM. HH:mm"
			);
		}
	}

	get teamOneString() {
		return format.formatTeamName(
			this.sportData.teamOneName,
			this.sportData.teamOneGender,
			this.sportData.eventType
		);
	}

	get teamTwoString() {
		return format.formatTeamName(
			this.sportData.teamTwoName,
			this.sportData.teamTwoGender,
			this.sportData.eventType
		);
	}

	scorePerPeriodString(liveResult?: EventOffer | null) {
		const sportData = liveResult || this.sportData;
		if (sportData.result?.scorePerPeriod == null) {
			return "";
		}

		const periodScores = sportData.result.scorePerPeriod
			.reduce((acc: string[], value: { home: number; away: number }) => {
				acc.push(`${value.home || 0}:${value.away || 0}`);
				return acc;
			}, [])
			.join("; ");

		return `(${periodScores})`;
	}

	get scorePerPeriodAtBetTimeString() {
		if (this.currentResult?.scorePerPeriod == null) {
			return "";
		}

		return this.currentResult.scorePerPeriod
			.reduce((acc: string[], value: { home: number; away: number }) => {
				acc.push(`(${value.home || 0}:${value.away || 0})`);
				return acc;
			}, [])
			.join(" ");
	}

	formattedCurrentResult(liveResult: EventOffer | null) {
		const sportData = liveResult || this.sportData;
		if (sportData?.result?.fullTimeScore != null) {
			return `${sportData.result.fullTimeScore.home || 0}:${
				sportData.result.fullTimeScore.away || 0
			}`;
		}

		if (sportData?.result?.currentScore != null) {
			return `${sportData.result.currentScore.home || 0}:${
				sportData.result.currentScore.away || 0
			}`;
		}

		return "";
	}

	get formattedResultAtBetTime() {
		if (this.currentResult?.currentScore != null) {
			return `${this.currentResult.currentScore.home || 0}:${
				this.currentResult.currentScore.away || 0
			}`;
		}

		return "";
	}

	get formattedBettingType() {
		const bettingType = this.nameProvider.getName(
			this.sportData.bettingTypeNameForBettingSlip,
			this.formattedOfferSpecifier
		);
		return bettingType;
	}

	get formattedBettingTypeTip() {
		if (
			this.sportData.eventType != undefined &&
			this.sportData.eventType != EventType.NORMAL
		) {
			if (this.playerId != null) {
				return `${this.playerLastName}, ${this.playerFirstName}`;
			} else if (this.teamId != null) {
				return this.teamName;
			}
			return this.bettingTypeTip;
		}
		const bettingTypeTip = this.nameProvider.getName(
			this.bettingTypeTip,
			this.formattedOfferSpecifier
		);
		return bettingTypeTip;
	}

	get formattedOfferSpecifier() {
		return {
			competitor1: this.sportData.teamOneName,
			competitor2: this.sportData.teamTwoName,
			...this.specifier,
		};
	}

	//#endregion getters
}
